// sort-imports-ignore
// This file is automatically compiled by Webpack, along with any other files
// present in this directory. You're encouraged to place your actual application logic in
// a relevant structure within app/javascript and only use these pack files to reference
// that code so it'll be compiled.
//
// To reference this file, add <%= javascript_pack_tag 'application' %> to the appropriate
// layout file, like app/views/layouts/application.html.erb

import 'core-js/stable';
import 'regenerator-runtime/runtime';

import * as Sentry from '@sentry/react';
import MainApp from 'main/startup/MainApp';
import mainStore from 'main/store/mainStore';
import ReactOnRails from 'react-on-rails/client';

import { getErrorTags, shouldIgnoreError } from 'my-core/error-utils';

if (__DEV__) {
  // Emotion's babel plugin can't label function-form `css` props, so opt into
  // runtime auto-labeling to keep generated class names readable in dev.
  globalThis.EMOTION_RUNTIME_AUTO_LABEL = true;
}

// delete window.IntersectionObserver;
// delete window.IntersectionObserverEntry;
if (!('IntersectionObserver' in window)) {
  window.IntersectionObserver = class IntersectionObserverPolyfillLight {
    constructor(cb) {
      this.callback = cb;
    }
    observe(element) {
      setTimeout(
        () => this.callback([{ isIntersecting: true, intersectionRatio: 1, isVisible: true, target: element }]),
        0,
      );
    }
    unobserve() {}
    disconnect() {}
  };
  import(/* webpackChunkName: "polyfills" */ 'intersection-observer');
}

// const DEBUG_AGENT = !!(window?.navigator?.userAgent?.includes('SM-A525F') || __DEV__);

if (process.env.SENTRY_CLIENT_DSN) {
  // https://docs.sentry.io/platforms/javascript/configuration/options/
  Sentry.init({
    dsn: process.env.SENTRY_CLIENT_DSN,
    release: process.env.HEROKU_SLUG_COMMIT,
    environment: process.env.NODE_ENV,
    // v11 defaults we rely on: `dataCollection` collects the IP address alongside the user id set in
    // tag-manager, and `attachStacktrace` gives `captureMessage` events their call site. Grouping then follows
    // the stack trace, so a call site that emits several distinct messages adds a `fingerprint`.
    // maxBreadcrumbs: DEBUG_AGENT ? 1000 : 100,
    // attachStacktrace: DEBUG_AGENT,

    // add event tags to sentry events. References:
    // https://docs.sentry.io/platforms/javascript/configuration/filtering/
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (shouldIgnoreError(error)) return null;
      event.tags = { ...event.tags, ...getErrorTags(error) };
      return event;
    },
    beforeBreadcrumb(event) {
      if (event.category === 'xhr' || event.category === 'fetch') {
        if (
          [
            /mixpanel/,
            /posthog/,
            /google-analytics/,
            /analytics\.google/,
            /doubleclick\.net/,
            /pagead2\.googlesyndication\.com/,
            /js\.intercomcdn.com/,
            /googletagmanager.com/,
            /gist\.build/, // Customer.io in-app messages
            /cloudfront\.net.*\.(ts|aac|vtt)$/, // HLS video segments fetched many times
          ].some(regex => regex.test(event.data.url))
        ) {
          return null;
        }
      }
      return event;
    },
    denyUrls: [
      // /extensions\//i,
      /^(chrome|safari)(-extension)?:\/\//i,
      'js.intercomcdn.com',
      'googletagmanager.com',
      'googleads.g.doubleclick.net',
      'pagead2.googlesyndication.com',
      // 'accounts.google.com', // temporarily unhide to debug google signin issue https://wize-lr.sentry.io/issues/2889128426/events/102c2cd0c5014cf297d0f59e47efe716/
      'analytics.tiktok.com',
      'js-agent.newrelic.com',
      'connect.facebook.net',
      /us-assets\.i\.posthog\.com/,
      /gist\.build/, // Customer.io in-app messages
    ],
  });
}

ReactOnRails.registerStore({ mainStore });

// This is how react_on_rails can see the MainApp in the browser.
ReactOnRails.register({ MainApp });

/* eslint-disable no-console */
setTimeout(() => {
  // Generated with https://mitos.shared.oxide.computer/
  const WIZE_LOGO_ART = `
@@@@@@@@                       ;;;;;;;               ;;;;;;;
@@@@@@@@@@                   ;;;;;;;;              ;;;;;;;;;
 @@@@@@@@@                  ;;;;;;;;              ;;;;;;;;;
  @@@@@@@@@                ;;;;;;;;               ;;;;;;;;
   @@@@@@@@@              ;;;;;;;;;              ;;;;;;;;
    @@@@@@@@@             ;;;;;;;;              ;;;;;;;;;
        @@@@@            ;;;;;;;;               ;;;;;;;;
                        ;;;;;;;;;              ;;;;;;;;
                       ;;;;;;;;;              ;;;;;;;;
                       ;;;;;;;;              ;;;;;;;;;
                      ;;;;;;;;               ;;;;;;;;
        ;;;;;;;;;    ;;;;;;;;; ;;;;;;;;;    ;;;;;;;;
         ;;;;;;;;;   ;;;;;;;;   ;;;;;;;;;  ;;;;;;;;;
          ;;;;;;;;; ;;;;;;;;    ;;;;;;;;; ;;;;;;;;;
          ;;;;;;;;;;;;;;;;;      ;;;;;;;;;;;;;;;;;
           ;;;;;;;;;;;;;;;;       ;;;;;;;;;;;;;;;
            ;;;;;;;;;;;;;;         ;;;;;;;;;;;;;;
             ;;;;;;;;;;;;          ;;;;;;;;;;;;;
             ;;;;;;;;;;;;           ;;;;;;;;;;;
`;

  // Wize brand colors: blue accent mark (@) + dark navy "W" (;)
  const BLUE = 'color:#377FFF';
  const NAVY = 'color:#3D4459';
  const NONE = 'color:inherit';
  const colorFor = char => {
    if (char === '@') return BLUE;
    if (char === ';' || char === '=') return NAVY;
    return NONE;
  };

  let format = '';
  const styles = [];
  let prevStyle = null;
  for (const char of WIZE_LOGO_ART) {
    if (char === '\n') {
      format += '\n';
      prevStyle = null;
      continue;
    }
    const style = colorFor(char);
    if (style !== prevStyle) {
      format += '%c';
      styles.push(`${style};font-weight:bold;font-family:monospace`);
      prevStyle = style;
    }
    format += char;
  }

  console.log(format, ...styles);
}, 1000);
/* eslint-enable no-console */
